import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MANAGER_PATHS } from "@/lib/roles";
import { biometricSupported, registerPasskey } from "@/lib/webauthn";
import {
  LayoutDashboard, CalendarDays, CalendarRange, BedDouble, Users, Wallet, FileBarChart2,
  Home, UserCog, Receipt, Landmark, Settings as SettingsIcon, LogOut, Building2,
  Ship, Menu, X, Megaphone, AlarmClock, CreditCard, Fingerprint, CalendarCheck, FileSpreadsheet, TrendingUp, Calculator, Camera, Coffee,
} from "lucide-react";

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/opzioni", label: "Opzioni prenotazione", icon: AlarmClock },
  { to: "/acconti-saldi", label: "Acconti / Saldi", icon: CreditCard },
  { to: "/annuale", label: "Annuale", icon: CalendarRange },
  { to: "/previsionale", label: "Previsionale", icon: TrendingUp },
  { to: "/preventivo", label: "Preventivatore", icon: Calculator },
  { to: "/foto", label: "Cloud foto", icon: Camera },
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/money", label: "Prenotazioni", icon: Wallet },
  { to: "/fatture", label: "Fatture", icon: FileSpreadsheet },
  { to: "/trasferimenti", label: "Trasferimenti", icon: Ship },
  { to: "/proprieta", label: "Proprietà", icon: BedDouble },
  { to: "/disponibilita", label: "Disponibilità", icon: CalendarCheck },
  { to: "/ospiti", label: "Ospiti", icon: Users },
  { to: "/report", label: "Report", icon: FileBarChart2 },
  { to: "/affitti", label: "Affitti", icon: Home },
  { to: "/staff", label: "Staff", icon: UserCog },
  { to: "/bar", label: "Bar e gadget", icon: Coffee },
  { to: "/spese-guesthouse", label: "Spese Guesthouse", icon: Receipt },
  { to: "/amministrazione", label: "Amministrazione", icon: Megaphone },
  { to: "/mira", label: "MIRA", icon: Landmark },
  { to: "/agenzie", label: "Agenzie", icon: Building2 },
  { to: "/utenti", label: "Utenti", icon: Users },
  { to: "/settings", label: "Impostazioni", icon: SettingsIcon },
];

const agencyNav = [
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/disponibilita", label: "Disponibilità", icon: CalendarCheck },
  { to: "/money", label: "Le mie prenotazioni", icon: Wallet },
];

const staffNav = [
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/trasferimenti", label: "Trasferimenti", icon: Ship },
];

export default function Layout() {
  const { user, logout, isAdmin, isStaff, isPhotographer, isManager, isBarman } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = isManager ? adminNav.filter((i) => MANAGER_PATHS.includes(i.to)) : isAdmin ? adminNav : isStaff ? staffNav : isPhotographer ? [{ to: "/foto", label: "Cloud foto", icon: Camera }] : isBarman ? [{ to: "/bar", label: "Bar e gadget", icon: Coffee }] : agencyNav;
  const roleLabel = isManager ? "Manager" : isAdmin ? "Amministratore" : isStaff ? "Staff" : isPhotographer ? "Fotografo" : isBarman ? "Barman" : "Agenzia";
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const enableBiometric = async () => {
    try {
      await registerPasskey();
      toast.success("Accesso biometrico attivato su questo dispositivo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile attivare l'accesso biometrico (annullato o non supportato)");
    }
  };

  if (isPhotographer && !location.pathname.startsWith("/foto")) return <Navigate to="/foto" replace />;
  if (isBarman && !location.pathname.startsWith("/bar")) return <Navigate to="/bar" replace />;


  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 z-30 bg-white border-b border-slate-200 flex items-center gap-3 px-4">
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2 -ml-2 text-slate-700 rounded-md hover:bg-slate-100">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-sky-700 grid place-items-center overflow-hidden p-1"><img src="/a20-travel-logo.png" alt="A20 TRAVEL" className="w-full h-full object-contain" /></div>
          <div className="font-heading font-extrabold text-slate-900 text-sm">A20 MNG</div>
        </div>
      </header>

      {/* Mobile overlay */}
      {open && (
        <div data-testid="sidebar-overlay" className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} />
      )}

      <aside
        className={`w-60 shrink-0 bg-white border-r border-slate-200 flex flex-col fixed h-screen z-50 transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200">
          <div className="w-9 h-9 rounded-md bg-sky-700 grid place-items-center overflow-hidden p-1">
            <img src="/a20-travel-logo.png" alt="A20 TRAVEL" className="w-full h-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-slate-900 text-sm">A20 MNG</div>
            <div className="text-[10px] text-slate-500 tracking-wide">GUESTHOUSE PMS</div>
          </div>
          <button
            data-testid="sidebar-close-btn"
            onClick={() => setOpen(false)}
            className="lg:hidden ml-auto p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                    isActive
                      ? "bg-sky-50 text-sky-700 font-semibold"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <Icon size={17} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="px-2 mb-2">
            <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 capitalize">{roleLabel}</div>
          </div>
          {biometricSupported() && (
            <button
              data-testid="enable-biometric-btn"
              onClick={enableBiometric}
              className="w-full flex items-center gap-2 px-3 py-2 mb-1 rounded-md text-sm text-slate-600 hover:bg-sky-50 hover:text-sky-700 transition-colors duration-150"
            >
              <Fingerprint size={16} /> Abilita impronta / Face ID
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors duration-150"
          >
            <LogOut size={16} /> Esci
          </button>
        </div>
      </aside>

      <main className="lg:ml-60 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
